import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Modal, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, addDoc, getDocs, query, orderBy, where, deleteDoc, doc, updateDoc, getDoc } from 'firebase/firestore';
import PortalHeader from '../../components/Portal/PortalHeader';
import TestModeBanner from '../../components/TestMode/TestModeBanner';
import { useTestMode } from '../../contexts/TestModeContext';
import { filterAvailableStudents } from '../../utils/studentFilters';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import { isAdminRole } from '../../utils/permissions';

const ManageLessonsPage = () => {
	const { testMode } = useTestMode();
	const { userProfile } = useAuth();
	// lessons delete is admin-only (firestore.rules); mirror it so teachers
	// don't see a Delete button that fails at the server (#25).
	const isAdmin = isAdminRole(userProfile);
	const navigate = useNavigate();
	const [students, setStudents] = useState([]);
	const [lessons, setLessons] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [editingLesson, setEditingLesson] = useState(null);
	const [alert, setAlert] = useState({ show: false, message: '', variant: '' });

	// Form state
	const [formData, setFormData] = useState({
		studentId: '',
		date: '',
		time: '',
		duration: 45,
		lessonType: 'Private',
		instrument: '',
		repertoire: '',
		location: 'Lakeville'
	});

	// Generate time options in 15-minute increments from 9 AM to 9 PM
	const generateTimeOptions = () => {
		const times = [];
		for (let hour = 9; hour <= 21; hour++) {
			for (let minute = 0; minute < 60; minute += 15) {
				if (hour === 21 && minute > 0) break; // Stop at 9:00 PM
				const time24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
				const hourDisplay = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
				const ampm = hour >= 12 ? 'PM' : 'AM';
				const time12 = `${hourDisplay}:${minute.toString().padStart(2, '0')} ${ampm}`;
				times.push({ value: time24, label: time12 });
			}
		}
		return times;
	};

	useEffect(() => {
		fetchStudents();
		fetchUpcomingLessons();
	}, [testMode]); // Refetch students when test mode changes

	const fetchStudents = async () => {
		try {
			const studentsQuery = query(collection(db, 'students'), orderBy('lastName'));
			const snapshot = await getDocs(studentsQuery);
			const allStudents = snapshot.docs.map(doc => ({
				id: doc.id,
				...doc.data()
			}));

			// Use filter based on test mode
			const studentsData = filterAvailableStudents(allStudents, testMode);
			setStudents(studentsData);
		} catch (error) {
			console.error('Error fetching students:', error);
			showAlert('Error loading students', 'danger');
		}
	};

	const fetchUpcomingLessons = async () => {
		try {
			const today = new Date().toISOString().split('T')[0];
			const lessonsQuery = query(
				collection(db, 'lessons'),
				where('date', '>=', today),
				orderBy('date'),
				orderBy('time')
			);

			const snapshot = await getDocs(lessonsQuery);
			const lessonsData = await Promise.all(
				snapshot.docs.map(async (lessonDoc) => {
					const lessonData = lessonDoc.data();

					// Fetch student name - use lesson's studentName as default
					let studentName = lessonData.studentName || 'Unknown Student';

					// Try to get more detailed info from students collection if linked
					if (lessonData.studentId) {
						try {
							const studentDoc = await getDoc(doc(db, 'students', lessonData.studentId));
							if (studentDoc.exists()) {
								const studentData = studentDoc.data();
								studentName = studentData.fullName || studentData.name || `${studentData.firstName} ${studentData.lastName}`;
							}
						} catch (err) {
							console.error('Error fetching student:', err);
						}
					}

					return {
						id: lessonDoc.id,
						...lessonData,
						studentName
					};
				})
			);

			setLessons(lessonsData);
		} catch (error) {
			console.error('Error fetching lessons:', error);
			showAlert('Error loading lessons', 'danger');
		} finally {
			setLoading(false);
		}
	};

	const showAlert = (message, variant = 'success') => {
		setAlert({ show: true, message, variant });
		setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 4000);
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		// Validation
		if (!formData.studentId || !formData.date || !formData.time) {
			showAlert('Please fill in all required fields', 'danger');
			return;
		}

		try {
			const lessonData = {
				studentId: formData.studentId,
				date: formData.date,
				time: formData.time,
				duration: parseInt(formData.duration),
				lessonType: formData.lessonType,
				instrument: formData.instrument,
				repertoire: formData.repertoire
					? formData.repertoire.split(',').map(s => s.trim()).filter(s => s)
					: [],
				location: formData.location,
				status: 'scheduled'
			};

			let lessonId;
			if (editingLesson) {
				// Update existing lesson
				await updateDoc(doc(db, 'lessons', editingLesson.id), lessonData);
				lessonId = editingLesson.id;
				showAlert('Lesson updated successfully!', 'success');
			} else {
				// Add new lesson
				const docRef = await addDoc(collection(db, 'lessons'), lessonData);
				lessonId = docRef.id;
				showAlert('Lesson added successfully! Click the green dashboard button to view it.', 'success');
			}

			// Reset form and close modal
			setFormData({
				studentId: '',
				date: '',
				time: '',
				duration: 45,
				lessonType: 'Private',
				instrument: '',
				repertoire: '',
				location: 'Lakeville'
			});
			setEditingLesson(null);
			setShowModal(false);

			// Refresh lessons list
			await fetchUpcomingLessons();
		} catch (error) {
			console.error('Error saving lesson:', error);
			showAlert('Error saving lesson. Please try again.', 'danger');
		}
	};

	const handleEdit = (lesson) => {
		setEditingLesson(lesson);
		setFormData({
			studentId: lesson.studentId,
			date: lesson.date,
			time: lesson.time,
			duration: lesson.duration || 45,
			lessonType: lesson.lessonType || 'Private',
			instrument: lesson.instrument || '',
			repertoire: Array.isArray(lesson.repertoire) ? lesson.repertoire.join(', ') : '',
			location: lesson.location || 'Lakeville'
		});
		setShowModal(true);
	};

	const handleDelete = async (lessonId) => {
		if (!window.confirm('Are you sure you want to delete this lesson?')) {
			return;
		}

		try {
			await deleteDoc(doc(db, 'lessons', lessonId));
			showAlert('Lesson deleted successfully!', 'success');
			await fetchUpcomingLessons();
		} catch (error) {
			console.error('Error deleting lesson:', error);
			showAlert('Error deleting lesson. Please try again.', 'danger');
		}
	};

	const handleModalClose = () => {
		setShowModal(false);
		setEditingLesson(null);
		setFormData({
			studentId: '',
			date: '',
			time: '',
			duration: 45,
			lessonType: 'Private',
			instrument: '',
			repertoire: '',
			location: 'Lakeville'
		});
	};

	const formatDate = (dateStr) => {
		const date = new Date(dateStr + 'T00:00:00');
		return date.toLocaleDateString('en-US', {
			weekday: 'short',
			month: 'short',
			day: 'numeric'
		});
	};

	const formatTime = (time24) => {
		const [hours, minutes] = time24.split(':');
		const hour = parseInt(hours);
		const ampm = hour >= 12 ? 'PM' : 'AM';
		const hour12 = hour % 12 || 12;
		return `${hour12}:${minutes} ${ampm}`;
	};

	if (loading) {
		return (
			<>
				<PortalHeader title="Manage Lessons" subtitle="Add, edit, and organize lessons" />
				<TestModeBanner />
				<Container className='py-4'>
					<div className='text-center'>Loading...</div>
				</Container>
			</>
		);
	}

	return (
		<>
			<PortalHeader title="Manage Lessons" subtitle="Add, edit, and organize lessons" />
			<TestModeBanner />
			<Container className='py-4'>
				{alert.show && (
					<Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false, message: '', variant: '' })}>
						{alert.message}
					</Alert>
				)}

				<Row className='g-4'>
					<Col lg={12}>
						<Card className='shadow-sm'>
							<Card.Header className='d-flex justify-content-between align-items-center bg-transparent'>
								<h5 className='mb-0'>
									<i className='fa-sharp fa-solid fa-calendar-plus me-2' style={{ color: '#384971' }}></i>
									Upcoming Lessons
									<Badge bg='secondary' className='ms-2'>{lessons.length}</Badge>
								</h5>
								<Button
									variant='primary'
									onClick={() => setShowModal(true)}
								>
									<i className='fa-sharp fa-solid fa-plus me-2'></i>
									Add Lesson
								</Button>
							</Card.Header>
							<Card.Body className='p-0'>
								{lessons.length === 0 ? (
									<div className='text-center py-5 text-muted'>
										<i className='fa-sharp fa-solid fa-calendar-xmark fa-3x mb-3 d-block' style={{ opacity: 0.3 }}></i>
										<p>No upcoming lessons scheduled</p>
										<Button variant='primary' onClick={() => setShowModal(true)}>
											Add Your First Lesson
										</Button>
									</div>
								) : (
									<div className='table-responsive'>
										<Table hover className='mb-0'>
											<thead className='table-light'>
												<tr>
													<th>Date</th>
													<th>Time</th>
													<th>Student</th>
													<th>Type</th>
													<th>Duration</th>
													<th>Actions</th>
												</tr>
											</thead>
											<tbody>
												{lessons.map((lesson) => (
													<tr key={lesson.id}>
														<td className='align-middle'>
															<strong>{formatDate(lesson.date)}</strong>
														</td>
														<td className='align-middle'>
															{formatTime(lesson.time)}
														</td>
														<td className='align-middle'>
															<div>
																<strong>{lesson.studentName}</strong>
																{lesson.instrument && (
																	<>
																		<br />
																		<small className='text-muted'>{lesson.instrument}</small>
																	</>
																)}
															</div>
														</td>
														<td className='align-middle'>
															<Badge bg='light' text='dark'>
																{lesson.lessonType || 'Private'}
															</Badge>
														</td>
														<td className='align-middle'>
															{lesson.duration || 45} min
														</td>
														<td className='align-middle'>
															<div className='d-flex gap-2'>
																<Button
																	variant='success'
																	size='sm'
																	onClick={() => navigate(`/portal/work/lesson/${lesson.id}`)}
																	title='View Lesson Dashboard'
																>
																	<i className='fa-sharp fa-solid fa-chalkboard-user'></i>
																</Button>
																<Button
																	variant='outline-primary'
																	size='sm'
																	onClick={() => handleEdit(lesson)}
																	title='Edit Lesson'
																>
																	<i className='fa-sharp fa-solid fa-edit'></i>
																</Button>
																{isAdmin && (
																	<Button
																		variant='outline-danger'
																		size='sm'
																		onClick={() => handleDelete(lesson.id)}
																		title='Delete Lesson'
																	>
																		<i className='fa-sharp fa-solid fa-trash'></i>
																	</Button>
																)}
															</div>
														</td>
													</tr>
												))}
											</tbody>
										</Table>
									</div>
								)}
							</Card.Body>
						</Card>
					</Col>
				</Row>

				{/* Add/Edit Lesson Modal */}
				<Modal show={showModal} onHide={handleModalClose} size='lg'>
					<Modal.Header closeButton>
						<Modal.Title>
							{editingLesson ? 'Edit Lesson' : 'Add New Lesson'}
						</Modal.Title>
					</Modal.Header>
					<Form onSubmit={handleSubmit}>
						<Modal.Body>
							<Row className='g-3'>
								<Col md={12}>
									<Form.Group>
										<Form.Label>Student <span className='text-danger'>*</span></Form.Label>
										<Form.Select
											name='studentId'
											value={formData.studentId}
											onChange={handleInputChange}
											required
										>
											<option value=''>Select a student...</option>
											{students
												.sort((a, b) => {
													// Sort: Active students first, then inactive
													if (a.status === 'Active' && b.status !== 'Active') return -1;
													if (a.status !== 'Active' && b.status === 'Active') return 1;
													return 0;
												})
												.map((student) => {
													const name = student.fullName || `${student.firstName} ${student.lastName}`;
													const isInactive = student.status !== 'Active';
													return (
														<option
															key={student.id}
															value={student.id}
															style={isInactive ? { color: '#6c757d', fontStyle: 'italic' } : {}}
														>
															{name}{isInactive ? ' (Inactive)' : ''}
														</option>
													);
												})}
										</Form.Select>
										<Form.Text className='text-muted'>
											<i className='fa-sharp fa-solid fa-info-circle me-1'></i>
											{testMode
												? 'Test mode: Only test students are shown'
												: 'Locked, archived, and test students are hidden from this list'}
										</Form.Text>
									</Form.Group>

									{/* Warning for inactive students */}
									{formData.studentId && students.find(s => s.id === formData.studentId)?.status !== 'Active' && (
										<Alert variant='warning' className='mt-2'>
											<i className='fa-sharp fa-solid fa-exclamation-triangle me-2'></i>
											<strong>Warning:</strong> This student is marked as inactive. Consider verifying their status before scheduling.
										</Alert>
									)}
								</Col>

								<Col md={6}>
									<Form.Group>
										<Form.Label>Date <span className='text-danger'>*</span></Form.Label>
										<Form.Control
											type='date'
											name='date'
											value={formData.date}
											onChange={handleInputChange}
											required
										/>
									</Form.Group>
								</Col>

								<Col md={6}>
									<Form.Group>
										<Form.Label>Time <span className='text-danger'>*</span></Form.Label>
										<Form.Select
											name='time'
											value={formData.time}
											onChange={handleInputChange}
											required
										>
											<option value=''>Select a time...</option>
											{generateTimeOptions().map((time) => (
												<option key={time.value} value={time.value}>
													{time.label}
												</option>
											))}
										</Form.Select>
									</Form.Group>
								</Col>

								<Col md={6}>
									<Form.Group>
										<Form.Label>Duration (minutes)</Form.Label>
										<Form.Control
											type='number'
											name='duration'
											value={formData.duration}
											onChange={handleInputChange}
											min='15'
											step='15'
										/>
									</Form.Group>
								</Col>

								<Col md={6}>
									<Form.Group>
										<Form.Label>Lesson Type</Form.Label>
										<Form.Select
											name='lessonType'
											value={formData.lessonType}
											onChange={handleInputChange}
										>
											<option value='Private'>Private</option>
											<option value='Group'>Group</option>
											<option value='Ensemble'>Ensemble</option>
											<option value='Workshop'>Workshop</option>
										</Form.Select>
									</Form.Group>
								</Col>

								<Col md={12}>
									<Form.Group>
										<Form.Label>Location <span className='text-danger'>*</span></Form.Label>
										<div className='d-flex gap-4 mt-2'>
											<Form.Check
												type='radio'
												id='location-lakeville'
												name='location'
												label='Lakeville'
												value='Lakeville'
												checked={formData.location === 'Lakeville'}
												onChange={handleInputChange}
											/>
											<Form.Check
												type='radio'
												id='location-headline'
												name='location'
												label='Headline'
												value='Headline'
												checked={formData.location === 'Headline'}
												onChange={handleInputChange}
											/>
										</div>
									</Form.Group>
								</Col>

								<Col md={12}>
									<Form.Group>
										<Form.Label>Instrument</Form.Label>
										<Form.Control
											type='text'
											name='instrument'
											value={formData.instrument}
											onChange={handleInputChange}
											placeholder='e.g., Voice, Piano, Guitar'
										/>
									</Form.Group>
								</Col>

								<Col md={12}>
									<Form.Group>
										<Form.Label>Repertoire (comma-separated)</Form.Label>
										<Form.Control
											as='textarea'
											rows={3}
											name='repertoire'
											value={formData.repertoire}
											onChange={handleInputChange}
											placeholder='e.g., Amazing Grace, Let It Be, Hallelujah'
										/>
										<Form.Text className='text-muted'>
											Separate song titles with commas
										</Form.Text>
									</Form.Group>
								</Col>
							</Row>
						</Modal.Body>
						<Modal.Footer>
							<Button variant='secondary' onClick={handleModalClose}>
								Cancel
							</Button>
							<Button variant='primary' type='submit'>
								{editingLesson ? 'Update Lesson' : 'Add Lesson'}
							</Button>
						</Modal.Footer>
					</Form>
				</Modal>
			</Container>
		</>
	);
};

export default ManageLessonsPage;
